// CSR-2063 — the value set the ACORD form editor edits, and what counts as an
// edit.
//
// Kept apart from the components because two behaviours in the design spec turn
// on it and both are worth testing on their own: the values SENT are the whole
// form rather than a diff (the server writes exactly what it receives and never
// merges with the prefill), and the unsaved-changes warning compares against the
// values the form OPENED with, not against empty.
import type { AcordFormPrefill, AcordFormValues } from "@/lib/api";

/**
 * The values the form opens with: whatever the paper already says, with the AMS
 * prefill written over it.
 *
 * Prefill wins on conflict — a blank the mapping fills is a blank AMS knows the
 * answer to. `templateText` is what `collectAcordTemplateText` found, which across
 * the eight templates is the ACORD 125 edition marker and nothing else; it is part
 * of the set because saving sends the whole form, and a value left out is a value
 * erased.
 */
export function initialAcordFormValues(
  templateText: Record<string, string>,
  prefill: Pick<AcordFormPrefill, "text" | "checks">,
): AcordFormValues {
  return {
    text: { ...templateText, ...prefill.text },
    checks: { ...prefill.checks },
    radios: {},
  };
}

function bagsDiffer<T extends string | boolean>(
  original: Record<string, T>,
  current: Record<string, T>,
  absent: T,
): boolean {
  // Union of both sides: a value the agent typed into a blank the form opened
  // without is an edit, and so is one they cleared.
  const names = Object.keys(original).concat(Object.keys(current));
  return names.some((name) => (current[name] ?? absent) !== (original[name] ?? absent));
}

/**
 * Has the agent changed anything since the form opened?
 *
 * Drives the unsaved-changes warning. Downloading deliberately does NOT reset the
 * baseline — a local copy is not a filing, so the warning still fires afterwards.
 */
export function isAcordFormEdited(
  original: AcordFormValues,
  current: AcordFormValues,
): boolean {
  return (
    bagsDiffer(original.text, current.text, "") ||
    bagsDiffer(original.checks, current.checks, false) ||
    bagsDiffer(original.radios, current.radios, "")
  );
}
